import { Button, Form, Progress } from "@repo/ui";
import { useFormStore } from "../../stores/formStore";
import { useFormPageNavigation } from "../../hooks/useFormPageNavigation";
import { FormTransitionOverlay } from "../../components/FormTransitionOverlay";
import { cn, versionCopsoq3DeDonnees } from "@repo/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "convex/react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { api } from "@convex/_generated/api";
import { TemplateFormEnum, TypeEvalEnum } from "@convex/schema";
import {
  isBackendError,
  type Copsoq3Reponse,
  type FormTemplateProps,
} from "../../lib/types";
import type { Copsoq3Question } from "./copsoq3-pompiers-data";
import { QUESTIONNAIRES_COPSOQ3 } from "./copsoq3-versions";
import {
  depuisReponses,
  ecranComplet,
  situerPage,
  versReponses,
  type EcranQuestions,
  type SaisieEcrans,
  type ValeurSaisie,
} from "./copsoq3-ecrans";
import { saisiePartie2Initiale, versReponsesPartie2 } from "./copsoq3-partie2";
import { Copsoq3Ecran } from "./copsoq3-ecran";
import { Copsoq3QuestionPartie2 } from "./copsoq3-question";

const schemaPartie2 = (questions: readonly Copsoq3Question[]) =>
  z.object(
    Object.fromEntries(
      questions.map((q) => [
        q.name,
        z.string({ required_error: "Reponse requise." }).min(1),
      ]),
    ),
  );

type FormData = z.infer<ReturnType<typeof schemaPartie2>>;

const SOUS_TITRES = {
  1: "Partie 1 — Questions sociodémographiques et professionnelles",
  2: "Partie 2 — Conditions de travail",
  3: "Partie 3 — Questions médicales",
} as const;

/** Version sans parties 1 et 3 : la partie 2 est tout le questionnaire. */
const SOUS_TITRE_PARTIE_UNIQUE = "Conditions de travail";

const AUCUN_ECRAN: readonly EcranQuestions[] = [];

export function CandidateCopsoq3Template({
  token,
  parcoursId,
  editable,
  evaluation,
  onComplete,
}: FormTemplateProps) {
  const { isSubmitting, setIsSubmitting } = useFormStore();

  const [slideClass, setSlideClass] = useState("animate-slide-in-right");
  const [animKey, setAnimKey] = useState(0);
  const [saisie, setSaisie] = useState<SaisieEcrans>(() =>
    depuisReponses(evaluation.donnees.profil, evaluation.donnees.sante),
  );

  const submitEvaluation = useMutation(api.evaluations.soumettreEvaluation);
  const updateResponses = useMutation(api.evaluations.mettreAJourReponses);

  // La version est figée par la campagne : le formulaire ne la renvoie pas,
  // le serveur garde celle stockée.
  const questionnaire =
    QUESTIONNAIRES_COPSOQ3[versionCopsoq3DeDonnees(evaluation.donnees)];
  const { questions } = questionnaire;
  const schema = useMemo(() => schemaPartie2(questions), [questions]);

  // Une passation terminée avant l'ajout d'une partie n'en a pas les
  // réponses : sa relecture saute cette partie.
  const { profil: profilEnBase, sante: santeEnBase } = evaluation.donnees;
  const partie1 =
    editable || profilEnBase !== undefined ? questionnaire.partie1 : AUCUN_ECRAN;
  const partie3 =
    editable || santeEnBase !== undefined ? questionnaire.partie3 : AUCUN_ECRAN;
  const partieUnique =
    questionnaire.partie1.length === 0 && questionnaire.partie3.length === 0;
  const totalQuestions = questions.length;
  const pageCount = partie1.length + totalQuestions + partie3.length;

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: saisiePartie2Initiale(
      questions,
      Object.values(evaluation.donnees.reponses) as Copsoq3Reponse[],
    ),
  });

  const buildDonnees = (data: FormData, pageActuelle?: number) => {
    const profil = versReponses(saisie, questionnaire.partie1);
    const sante = versReponses(saisie, questionnaire.partie3);
    return {
      type: TypeEvalEnum.FORM as const,
      template: TemplateFormEnum.COPSOQ3,
      ...(pageActuelle !== undefined && { pageActuelle }),
      ...(profil.length > 0 && { profil }),
      ...(sante.length > 0 && { sante }),
      reponses: versReponsesPartie2(questions, data),
    };
  };

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    try {
      const result = await submitEvaluation({
        evaluationId: evaluation._id,
        parcoursId,
        token,
        donnees: buildDonnees(data),
      });
      if (isBackendError(result)) {
        console.error("Erreur:", result.erreur);
      } else {
        onComplete?.();
      }
    } catch (error) {
      console.error("Erreur lors de la soumission:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveResponses = async (data: FormData, pageActuelle: number) => {
    try {
      await updateResponses({
        evaluationId: evaluation._id,
        parcoursId,
        token,
        donnees: buildDonnees(data, pageActuelle),
      });
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
    }
  };

  // L'animation de slide est jouée juste avant le changement d'écran, dans
  // le sens de la navigation.
  const onPageChange = useCallback(
    ({ direction }: { direction: "next" | "prev" }) => {
      setSlideClass(
        direction === "next"
          ? "animate-slide-in-right"
          : "animate-slide-in-left",
      );
      setAnimKey((k) => k + 1);
    },
    [],
  );

  const {
    currentPage: currentStep,
    isNavigating,
    showTransitionOverlay,
    goToPage,
  } = useFormPageNavigation<FormData>({
    // Les écrans de la partie 1, une question de la partie 2 par écran, puis
    // les écrans de la partie 3.
    pageCount,
    // Dans une version à partie 1, un `pageActuelle` sans profil en base ne
    // peut venir que d'une passation commencée avant la partie 1 : il désigne
    // une question, pas un écran. On repart du début pour faire remplir la
    // partie 1.
    pageActuelle:
      questionnaire.partie1.length > 0 && profilEnBase === undefined
        ? undefined
        : evaluation.donnees.pageActuelle,
    save: saveResponses,
    onPageChange,
  });

  const handleNext = async () => {
    await goToPage(currentStep + 1, form.getValues());
  };

  const handlePrev = async () => {
    await goToPage(currentStep - 1, form.getValues());
  };

  const changerSaisie = (name: string, valeur: ValeurSaisie) =>
    setSaisie((s) => ({ ...s, [name]: valeur }));

  const page = situerPage(currentStep, partie1, totalQuestions, partie3);
  const currentQuestion =
    page?.partie === 2 ? questions[page.index] : undefined;
  const ecransPartie =
    page?.partie === 3 ? questionnaire.partie3 : questionnaire.partie1;

  const reponsesPartie2 = form.watch();
  const hasAnswer =
    page?.partie === 2
      ? !!currentQuestion && !!reponsesPartie2[currentQuestion.name]
      : !!page && ecranComplet(page.ecran, saisie, ecransPartie);

  if (!page) return <div>Chargement...</div>;

  const progressPercentage = ((currentStep + 1) / pageCount) * 100;
  const isFirstPage = currentStep === 0;
  const isLastPage = currentStep === pageCount - 1;

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-8 w-full max-w-2xl mx-auto py-8"
      >
        <div className="space-y-1">
          <h1 className="text-2xl font-bold">COPSOQ III</h1>
          <p className="text-muted-foreground text-sm">
            {partieUnique ? SOUS_TITRE_PARTIE_UNIQUE : SOUS_TITRES[page.partie]}
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>
              {page.partie === 2
                ? `Question ${page.index + 1} / ${page.nb}`
                : `Étape ${page.index + 1} / ${page.nb}`}
            </span>
            <span>{Math.round(progressPercentage)}%</span>
          </div>
          <Progress value={progressPercentage} className="h-1.5" />
        </div>

        <div
          key={animKey}
          className={cn(
            "transition-opacity",
            slideClass,
            isNavigating && "pointer-events-none opacity-50",
          )}
          aria-busy={isNavigating}
        >
          {page.partie !== 2 ? (
            <Copsoq3Ecran
              ecran={page.ecran}
              ecrans={ecransPartie}
              saisie={saisie}
              editable={editable}
              onChange={changerSaisie}
            />
          ) : (
            currentQuestion && (
              <Copsoq3QuestionPartie2
                key={currentQuestion.name}
                control={form.control}
                question={currentQuestion}
                editable={editable}
              />
            )
          )}
        </div>

        <div className="flex justify-between gap-4 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={handlePrev}
            disabled={isFirstPage || isNavigating}
            className="flex-1"
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Précédent
          </Button>

          {isLastPage ? (
            <Button
              type="submit"
              className="flex-1"
              disabled={isSubmitting || isNavigating || !editable || !hasAnswer}
            >
              {isSubmitting ? "Envoi en cours..." : "Soumettre"}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleNext}
              disabled={!hasAnswer || isNavigating}
              className="flex-1"
            >
              {isNavigating ? (
                <>
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  Suivant
                  <ChevronRight className="ml-1 h-4 w-4" />
                </>
              )}
            </Button>
          )}
        </div>

        <FormTransitionOverlay open={showTransitionOverlay} />
      </form>
    </Form>
  );
}
